import React from 'react'
import classNames from 'clsx'
import { withStyles } from '@material-ui/core/styles'
import {
  Grid,
  Table,
  Toolbar,
  TableFilterRow,
  TableHeaderRow,
} from '@devexpress/dx-react-grid-material-ui'
import { Table as TableMUI } from '@material-ui/core'
import { formatDate, formatDateTime, selectorFields } from '../../utils'
import DateRange from '@material-ui/icons/DateRange'
import { AsyncSelectPickerFilter } from './SelectPickerFilters'
import { DatePickerFilter, DateTimePickerFilter } from './DatePickerFilters'
import DebounceInput from '../DebounceInput'
import MoreOptions from './MoreOptions'

const styles = theme => ({
  table: {
    tableLayout: 'fixed',
    borderCollapse: 'separate',
    '& thead tr': {
      height: 41
    },
    '& thead th': {
      padding: '0px 4px',
      position: 'sticky',
      fallbacks: {
        position: '-webkit-sticky'
      },
    },
    '& thead tr:nth-child(2) th': {
      top: 41
    },
    '& thead th:not(:nth-child(1))': {
      borderLeft: '1px solid rgba(224, 224, 224, 1)'
    },
    '& tbody td:not(:nth-child(1))': {
      borderLeft: '1px solid rgba(224, 224, 224, 1)'
    },
    '& tbody tr:nth-of-type(odd)': {
      backgroundColor: '#f2f2f2'
    },
    '& tbody td': {
      zIndex: 0
    }
  },
  stickyTable: {
    position: 'sticky',
    zIndex: 500,
    overflow: 'visible',
    background: theme.palette.background.paper,
    fallbacks: {
      position: '-webkit-sticky'
    }
  },
})

const MyTable = ({ children, classes, className, use, tableRef, ...restProps }) => {
  return (
    <TableMUI
      ref={tableRef}
      stickyHeader
      aria-label="sticky table"
      className={classNames(
        {
          [classes.table]: true,
          [classes.stickyTable]: !!use,
          [classes.headTable]: use === 'head',
          [classes.footTable]: use === 'foot'
        },
        className
      )}
      {...restProps}
    >
      {children}
    </TableMUI>
  )
}
const tablePropsAreEql = (p, n) => {
  const condition0 = !p.children[0].props.columns.find((({ width, column, animationState }, idx) => {
    const c = n.children[0].props.columns[idx]
    return width !== c.width || column?.name !== c.column?.name || animationState?.transform !== c.animationState?.transform
  }))
  const condition1 = p.children[1].props.rows === n.children[1].props.rows
  const condition2 = p.children[2].props.rows === n.children[2].props.rows && p.children[2].props.columns === n.children[2].props.columns
  return condition0 && condition1 && condition2
}
const TableBase = React.memo(MyTable, tablePropsAreEql)
export const StickyTable = withStyles(styles, { name: 'Table' })(TableBase)

const MyToolbar = ({ children, ToolbarButtons, ...rest }) => {
  return (
    <Toolbar.Root style={{ minHeight: 0 }}>
      <ToolbarButtons {...rest} />
      {children}
    </Toolbar.Root>
  )
}
const toolbarPropsAreEql = (p, n) => p === n
export const ToolbarComponent = React.memo(MyToolbar, toolbarPropsAreEql)


const Root = props => <Grid.Root {...props} className="grid-root" style={{ flex: 1, overflowY: 'auto' }}/>
export const GridRoot = React.memo(Root, () => true)

const renderFilterRowChildren = (children, props) => {
  const columnName = props.column.name
  if (columnName !== 'edit' && columnName !== 'select') {
    const colInfo = props.columns.find(c => c.name === columnName) || {}
    if (selectorFields.includes(colInfo.type)) {
      return <AsyncSelectPickerFilter {...props} colInfo={colInfo}/>
    }
    
    switch (colInfo.type) {
    case 'date':
      return <DatePickerFilter {...props} colInfo={colInfo}/>
    
    case 'datetime':
      return <DateTimePickerFilter {...props} colInfo={colInfo}/>
    
    default:
      return <DebounceInput {...props} colInfo={colInfo}/>
    }
  }
}

const FilterRowCellComponent = ({ children, handleFilterChanged, ...props }) => {
  const style = props.column.name === 'edit' ? { left: 48 } : {}
  return (
    <TableFilterRow.Cell {...props} style={style}>
      {renderFilterRowChildren(children, { handleFilterChanged, ...props })}
    </TableFilterRow.Cell>
  )
}
const rowCellPropsAreEql = (p, n) => p.filter === n.filter
  && p.filter?.value === n.filter?.value
  && p.filter?.operation === n.filter?.operation
  && p.handleFilterChanged === n.handleFilterChanged
export const FilterRowCell = React.memo(FilterRowCellComponent, rowCellPropsAreEql)

export const FilterIcon = ({ type, ...restProps }) => type === 'month' ? <DateRange {...restProps} /> : null
export const FilterEditor = props => <TableFilterRow.Editor {...props} style={{ margin: 5 }}/>

export const HeaderSortLabel = props => (
  <TableHeaderRow.SortLabel {...props} getMessage={() => props.column.title}>
    {props.direction ? props.children : <span style={{ maxWidth: '100%' }}>{props.children.props.children}</span>}
  </TableHeaderRow.SortLabel>
)

export const getCellValue = ({ column, value, row, isAdmin, ...rest }) => {
  if (column.getCellValue) return column.getCellValue(row, column.name)
  switch (column.type) {
  case 'boolean':
    return <span>{value ? 'Yes' : value === false ? 'No' : ''}</span>
  case 'date':
    return value && formatDate(value)
  case 'datetime':
    return value && formatDateTime(value)
  case 'options':
    return (isAdmin || !row.Submitted) ? <MoreOptions row={row} /> : null
  default:
    return value
  }
}

const Cell = props => {
  const value = getCellValue(props)
  const colName = props.column.name
  const s = colName === 'select' ? { textOverflow: 'inherit' } : {}
  
  return (
    <Table.Cell
      {...props}
      style={{ ...props.style, padding: '2px 5px', zIndex: 200, ...s }}
    >
      {value}
    </Table.Cell>
  )
}
const cellPropsAreEql = (p, n) => p.value === n.value && p.tableColumn.wordWrapEnabled === n.tableColumn.wordWrapEnabled && p.row === n.row
export const CellComponent = React.memo(Cell, cellPropsAreEql)